import * as React from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { cn } from "../../lib/utils";
import { PopoverCloseContext } from "./popover";

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  fixedWeeks = true,
  captionLayout = "dropdown",
  fromYear = 2010,
  toYear = new Date().getFullYear() + 10,
  mode,
  onSelect,
  closeOnSelect = mode === "single",
  style,
  ...props
}) {
  const defaultClassNames = getDefaultClassNames();
  const closePopover = React.useContext(PopoverCloseContext);

  const handleSelect = (selected, triggerDate, modifiers, event) => {
    onSelect?.(selected, triggerDate, modifiers, event);

    // Keep range and multiple calendars open so people can complete their choice.
    // A cleared single-date selection also stays open to avoid a surprising close.
    if (closeOnSelect && selected) closePopover();
  };

  return (
    <DayPicker
      mode={mode}
      showOutsideDays={showOutsideDays}
      fixedWeeks={fixedWeeks}
      captionLayout={captionLayout}
      fromYear={fromYear}
      toYear={toYear}
      className={cn(
        "w-[19rem] overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-card)] p-3 text-[var(--color-foreground)] shadow-[0_20px_44px_-22px_rgba(30,51,131,0.45)]",
        className
      )}
      style={{ width: "19rem", maxWidth: "calc(100vw - 1.5rem)", ...style }}
      classNames={{
        root: cn("w-[19rem]", defaultClassNames.root),
        months: cn("relative flex w-full flex-col gap-5 sm:flex-row", defaultClassNames.months),
        month: cn("flex w-full flex-col gap-4", defaultClassNames.month),
        month_caption: cn(
          "flex h-9 w-full items-center justify-center px-10",
          defaultClassNames.month_caption
        ),
        dropdowns: cn("flex items-center justify-center gap-2", defaultClassNames.dropdowns),
        dropdown_root: cn("relative inline-flex items-center", defaultClassNames.dropdown_root),
        dropdown: cn("peer absolute inset-0 z-10 cursor-pointer opacity-0", defaultClassNames.dropdown),
        months_dropdown: cn("min-w-[6.75rem]", defaultClassNames.months_dropdown),
        years_dropdown: cn("min-w-[4.75rem]", defaultClassNames.years_dropdown),
        caption_label: cn(
          "inline-flex h-8 items-center gap-1 rounded-lg border border-[color-mix(in_srgb,var(--color-primary)_16%,white)] bg-[color-mix(in_srgb,var(--color-workspace)_58%,white)] px-2.5 text-sm font-bold tracking-[-0.01em] text-[var(--color-primary)] shadow-[0_4px_12px_-9px_rgba(30,51,131,0.5)] transition-colors peer-focus-visible:ring-2 peer-focus-visible:ring-[var(--color-ring)] peer-focus-visible:ring-offset-2 [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:text-[var(--color-accent)]",
          defaultClassNames.caption_label
        ),
        nav: cn(
          "absolute inset-x-0 top-0 flex h-9 w-full items-center justify-between",
          defaultClassNames.nav
        ),
        button_previous: cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border)] hover:bg-[var(--color-workspace)] hover:text-[var(--color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-35",
          defaultClassNames.button_previous
        ),
        button_next: cn(
          "inline-flex h-8 w-8 items-center justify-center rounded-lg border border-transparent text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-border)] hover:bg-[var(--color-workspace)] hover:text-[var(--color-primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-35",
          defaultClassNames.button_next
        ),
        month_grid: cn("w-full table-fixed border-collapse", defaultClassNames.month_grid),
        weekdays: cn("flex", defaultClassNames.weekdays),
        weekday: cn(
          "w-9 pb-1 text-center text-[0.7rem] font-bold uppercase tracking-[0.08em] text-[var(--color-text-secondary)]",
          defaultClassNames.weekday
        ),
        week: cn("mt-1.5 flex h-9 w-full", defaultClassNames.week),
        day: cn("relative h-9 w-9 p-0 text-center text-sm", defaultClassNames.day),
        day_button: cn(
          "inline-flex h-9 w-9 items-center justify-center rounded-xl p-0 text-sm font-medium text-[var(--color-foreground)] transition-[background-color,color,transform,box-shadow] hover:-translate-y-px hover:bg-[color-mix(in_srgb,var(--color-primary)_10%,white)] hover:text-[var(--color-primary)] hover:font-semibold hover:shadow-sm focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:ring-offset-1",
          defaultClassNames.day_button
        ),
        selected: cn(
          "bg-[var(--color-primary)] text-[var(--color-primary-foreground)] shadow-sm",
          "[&_button]:font-semibold [&_button]:text-[var(--color-primary-foreground)] [&_button]:hover:bg-[var(--color-accent)] [&_button]:hover:text-[var(--color-accent-foreground)]",
          defaultClassNames.selected
        ),
        today: cn(
          "font-bold text-[var(--color-primary)] [&_button]:ring-1 [&_button]:ring-inset [&_button]:ring-[var(--color-primary)]",
          "aria-selected:bg-[var(--color-primary)] aria-selected:text-[var(--color-primary-foreground)] aria-selected:[&_button]:ring-0",
          defaultClassNames.today
        ),
        range_start: cn("rounded-l-lg bg-[var(--color-primary)]", defaultClassNames.range_start),
        range_end: cn("rounded-r-lg bg-[var(--color-primary)]", defaultClassNames.range_end),
        range_middle: cn(
          "rounded-none bg-[color-mix(in_srgb,var(--color-primary)_12%,white)] text-[var(--color-primary)] [&_button]:rounded-none [&_button]:text-[var(--color-primary)]",
          defaultClassNames.range_middle
        ),
        outside: cn(
          "text-[var(--color-text-secondary)]/35 aria-selected:text-[var(--color-text-secondary)]/35",
          defaultClassNames.outside
        ),
        disabled: cn(
          "cursor-not-allowed text-[var(--color-text-secondary)]/35 opacity-50 [&_button]:cursor-not-allowed [&_button]:hover:bg-transparent [&_button]:hover:text-inherit",
          defaultClassNames.disabled
        ),
        hidden: cn("invisible", defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Chevron: ({ className: chevronClassName, orientation, ...chevronProps }) => {
          const Icon = orientation === "down"
            ? ChevronDown
            : orientation === "left"
              ? ChevronLeft
              : ChevronRight;
          return <Icon className={cn("h-4 w-4", chevronClassName)} {...chevronProps} />;
        },
      }}
      {...props}
      onSelect={handleSelect}
    />
  );
}

Calendar.displayName = "Calendar";

export { Calendar };
